import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '.'
import { ENDPOINTS } from '../../lib/endpoints'

export interface SupportTicket {
  id:          string
  email:       string
  issueType:   string
  subject:     string
  description: string
  status:      string
  priority:    string
  adminNote:   string | null
  createdAt:   string
  updatedAt:   string
  user?: {
    id: string
    profile: { displayName: string; avatarUrl: string | null } | null
  } | null
}

export const supportApi = createApi({
  reducerPath: 'supportApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.accessToken
      if (token) headers.set('Authorization', `Bearer ${token}`)
      return headers
    },
  }),
  tagTypes: ['Support'],
  endpoints: (builder) => ({

    submitTicket: builder.mutation<{ ticket: SupportTicket; message: string }, {
      email: string; issueType: string; subject: string; description: string
    }>({
      query: (body) => ({ url: ENDPOINTS.support.submit, method: 'POST', body }),
      invalidatesTags: ['Support'],
    }),

    getAdminTickets: builder.query<{
      tickets: SupportTicket[]; total: number; page: number; limit: number; hasMore: boolean
    }, { status?: string; issueType?: string; page?: number; limit?: number }>({
      query: ({ status, issueType, page = 1, limit = 20 } = {}) => {
        const params = new URLSearchParams()
        if (status)    params.set('status', status)
        if (issueType) params.set('issueType', issueType)
        params.set('page', String(page))
        params.set('limit', String(limit))
        return `${ENDPOINTS.support.adminList}?${params}`
      },
      providesTags: ['Support'],
    }),

    updateTicket: builder.mutation<{ ticket: SupportTicket }, {
      id: string; status?: string; priority?: string; adminNote?: string
    }>({
      query: ({ id, ...body }) => ({ url: ENDPOINTS.support.adminUpdate(id), method: 'PUT', body }),
      invalidatesTags: ['Support'],
    }),

    getSupportStats: builder.query<{
      byStatus: Record<string, number>; byType: Record<string, number>
    }, void>({
      query: () => ENDPOINTS.support.adminStats,
      providesTags: ['Support'],
    }),

  }),
})

export const {
  useSubmitTicketMutation,
  useGetAdminTicketsQuery,
  useUpdateTicketMutation,
  useGetSupportStatsQuery,
} = supportApi
